import { useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Calendar,
  Clock,
  AlertTriangle,
  RefreshCw,
  Loader2,
  CheckCircle2,
  TrendingUp,
  TrendingDown,
  Minus,
  Moon,
  Activity,
  Pill,
  Heart,
  History,
  Utensils,
  Users,
  Brain,
  Dumbbell,
  MessageCircle,
} from 'lucide-react'
import { usePatientSummaries } from '../hooks/use-patient-detail'
import { format, isValid, parseISO } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { useQueryClient } from '@tanstack/react-query'
import { useToast } from '@/hooks/use-toast'
import { supabase } from '@/lib/supabase'
import { getApiBaseUrl } from '@/lib/api-url'

interface PatientSummariesTabProps {
  patientId: string
}

// Tipos para os campos JSONB
interface MoodAnalysis {
  overall?: string
  trend?: string
  score?: number
  averageSentiment?: number
  // Campos com scores 1-5
  averageMood?: number
  averageSleep?: number
  averageAppetite?: number
  averageSocial?: number
  trendDirection?: string
  distribution?: {
    positive?: number
    neutral?: number
    negative?: number
  }
  description?: string
  highlights?: string[]
  isHistoricalData?: boolean
  weeksBack?: number
}

interface MedicationAdherence {
  mentioned?: string[]
  percentage?: number
  status?: string
  notes?: string
  concerns?: string[]
  adherenceLevel?: string
}

interface SleepPatterns {
  mentioned?: boolean
  quality?: string
  average_hours?: number
  averageHours?: number
  duration?: string
  issues?: string[]
  notes?: string
}

interface ActivityLevels {
  mentioned?: boolean
  overall?: string
  trend?: string
  highlights?: string[]
  notes?: string
  physical?: string
  social?: string
  cognitive?: string
}

const generationTypeLabels = {
  automatic: 'Automatico',
  manual: 'Manual',
}

const generationTypeColors = {
  automatic: 'text-blue-600 bg-blue-100',
  manual: 'text-purple-600 bg-purple-100',
}

// Traduzir valores em inglês para português
function translateValue(value: string | undefined | null): string {
  if (!value) return ''
  const translations: Record<string, string> = {
    // Trends
    'improving': 'Melhorando',
    'declining': 'Piorando',
    'stable': 'Estavel',
    'insufficient_data': 'Dados insuficientes',
    // Mood
    'positive': 'Positivo',
    'negative': 'Negativo',
    'neutral': 'Neutro',
    // Quality
    'good': 'Boa',
    'bad': 'Ruim',
    'regular': 'Regular',
    'fair': 'Regular',
    'poor': 'Ruim',
    'unknown': 'Desconhecido',
    // Activity
    'high': 'Alto',
    'low': 'Baixo',
    'moderate': 'Moderado',
    'medium': 'Moderado',
    // Activity levels specific
    'active': 'Ativo',
    'withdrawn': 'Retraido',
    'alert': 'Alerta',
    'impaired': 'Comprometido',
    // Duration
    'adequate': 'Adequada',
    'insufficient': 'Insuficiente',
    'excessive': 'Excessiva',
  }
  const lower = value.toLowerCase()
  return translations[lower] || value
}

function getTrendIcon(trend: string | undefined) {
  if (!trend) return <Minus className="h-4 w-4 text-gray-400" />
  const t = trend.toLowerCase()
  if (t.includes('melhor') || t.includes('improv') || t.includes('up')) {
    return <TrendingUp className="h-4 w-4 text-green-500" />
  }
  if (t.includes('pior') || t.includes('declin') || t.includes('down')) {
    return <TrendingDown className="h-4 w-4 text-red-500" />
  }
  return <Minus className="h-4 w-4 text-gray-400" />
}

function formatSummaryDate(dateValue: string | null | undefined): string {
  if (!dateValue) return 'Data nao disponivel'
  const date = typeof dateValue === 'string' ? parseISO(dateValue) : new Date(dateValue)
  if (!isValid(date)) return 'Data invalida'
  return format(date, "d 'de' MMMM", { locale: ptBR })
}

function formatSummaryDateFull(dateValue: string | null | undefined): string {
  if (!dateValue) return 'Data nao disponivel'
  const date = typeof dateValue === 'string' ? parseISO(dateValue) : new Date(dateValue)
  if (!isValid(date)) return 'Data invalida'
  return format(date, "d 'de' MMMM, yyyy", { locale: ptBR })
}

// Componente de barra de score 1-5
function ScoreBar({ score, label, icon: Icon, color }: {
  score: number | undefined
  label: string
  icon: React.ElementType
  color: string
}) {
  const safeScore = score !== undefined ? Math.min(5, Math.max(1, score)) : undefined
  const percentage = safeScore !== undefined ? (safeScore / 5) * 100 : 0

  const getScoreColor = (s: number) => {
    if (s >= 4) return 'bg-green-500'
    if (s >= 3) return 'bg-yellow-500'
    return 'bg-red-500'
  }

  const getScoreLabel = (s: number) => {
    if (s >= 4.5) return 'Excelente'
    if (s >= 3.5) return 'Bom'
    if (s >= 2.5) return 'Regular'
    if (s >= 1.5) return 'Ruim'
    return 'Muito Ruim'
  }

  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <Icon className={`h-3.5 w-3.5 ${color}`} />
          <span className="text-xs font-medium text-gray-700">{label}</span>
        </div>
        {safeScore !== undefined ? (
          <span className="text-xs font-semibold text-gray-800">
            {safeScore.toFixed(1)}/5 <span className="text-gray-500 font-normal">({getScoreLabel(safeScore)})</span>
          </span>
        ) : (
          <span className="text-xs text-gray-400">N/A</span>
        )}
      </div>
      <div className="w-full bg-gray-200 rounded-full h-2">
        {safeScore !== undefined && (
          <div
            className={`h-2 rounded-full transition-all ${getScoreColor(safeScore)}`}
            style={{ width: `${percentage}%` }}
          />
        )}
      </div>
    </div>
  )
}

// Componente para nivel de atividade (texto)
function ActivityLevel({ level, label, icon: Icon }: {
  level: string | undefined
  label: string
  icon: React.ElementType
}) {
  const getActivityBadgeColor = (l: string | undefined) => {
    if (!l) return 'bg-gray-100 text-gray-600'
    const lower = l.toLowerCase()
    if (lower === 'high' || lower === 'active' || lower === 'alert') return 'bg-green-100 text-green-700'
    if (lower === 'moderate') return 'bg-yellow-100 text-yellow-700'
    if (lower === 'low' || lower === 'withdrawn' || lower === 'impaired') return 'bg-red-100 text-red-700'
    return 'bg-gray-100 text-gray-600'
  }

  return (
    <div className="flex items-center justify-between py-1.5 border-b border-gray-100 last:border-0">
      <div className="flex items-center gap-1.5">
        <Icon className="h-3.5 w-3.5 text-gray-500" />
        <span className="text-xs text-gray-600">{label}</span>
      </div>
      <Badge className={`text-[10px] px-2 py-0.5 ${getActivityBadgeColor(level)}`}>
        {translateValue(level) || 'N/A'}
      </Badge>
    </div>
  )
}

export function PatientSummariesTab({ patientId }: PatientSummariesTabProps) {
  const { data: summaries, isLoading } = usePatientSummaries(patientId)
  const [isGenerating, setIsGenerating] = useState(false)
  const queryClient = useQueryClient()
  const { toast } = useToast()

  const handleGenerateSummary = async () => {
    setIsGenerating(true)
    try {
      const apiUrl = getApiBaseUrl()
      const { data: sessionData } = await supabase.auth.getSession()
      const response = await fetch(`${apiUrl}/api/whatsapp/weekly-summary/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${sessionData.session?.access_token}`,
        },
        body: JSON.stringify({ patientId }),
      })

      const result = await response.json()

      if (result.success) {
        toast({ title: 'Sucesso', description: 'Análise gerada com sucesso!' })
        queryClient.invalidateQueries({ queryKey: ['patient-summaries', patientId] })
      } else {
        toast({ title: 'Erro', description: result.message || 'Erro ao gerar análise', variant: 'destructive' })
      }
    } catch (error) {
      console.error('Erro ao gerar análise:', error)
      toast({ title: 'Erro', description: 'Erro ao conectar com o servidor', variant: 'destructive' })
    } finally {
      setIsGenerating(false)
    }
  }

  if (isLoading) {
    return <SummariesSkeleton />
  }

  return (
    <div className="space-y-6">
      {/* Header com botao de gerar */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium">Análises Semanais</h3>
          <p className="text-sm text-muted-foreground">
            Análises geradas automaticamente ou manualmente
          </p>
        </div>
        <Button
          onClick={handleGenerateSummary}
          disabled={isGenerating}
          variant="outline"
        >
          {isGenerating ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Gerando...
            </>
          ) : (
            <>
              <RefreshCw className="mr-2 h-4 w-4" />
              Gerar Análise Agora
            </>
          )}
        </Button>
      </div>

      {/* Lista de resumos */}
      {!summaries || summaries.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          <Calendar className="h-12 w-12 mx-auto mb-4 opacity-50" />
          <p className="text-lg font-medium">Nenhuma análise semanal encontrada</p>
          <p className="text-sm mt-1">
            Clique em "Gerar Análise Agora" para criar uma análise dos ultimos 7 dias
          </p>
        </div>
      ) : (
        summaries.map((summary) => {
          const generationType =
            (summary.generation_type as keyof typeof generationTypeLabels) ?? 'automatic'

          const moodAnalysis = summary.mood_analysis as MoodAnalysis | null
          const medicationAdherence = summary.medication_adherence as MedicationAdherence | null
          const sleepPatterns = summary.sleep_patterns as SleepPatterns | null
          const activityLevels = summary.activity_levels as ActivityLevels | null

          // Verificar se temos os novos campos de score 1-5
          const hasScores = moodAnalysis && (
            moodAnalysis.averageMood !== undefined ||
            moodAnalysis.averageSleep !== undefined ||
            moodAnalysis.averageAppetite !== undefined ||
            moodAnalysis.averageSocial !== undefined
          )

          return (
            <Card key={summary.id} className="hover:shadow-md transition-shadow">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg">
                    Semana de {formatSummaryDate(summary.week_start_date)} a{' '}
                    {formatSummaryDateFull(summary.week_end_date)}
                  </CardTitle>
                  <div className="flex items-center gap-2 flex-wrap">
                    {moodAnalysis?.isHistoricalData && (
                      <Badge className="bg-amber-100 text-amber-800">
                        <History className="h-3 w-3 mr-1" />
                        Dados de {moodAnalysis.weeksBack} semana{moodAnalysis.weeksBack && moodAnalysis.weeksBack > 1 ? 's' : ''} atras
                      </Badge>
                    )}
                    <Badge className={generationTypeColors[generationType]}>
                      <Clock className="h-3 w-3 mr-1" />
                      {generationTypeLabels[generationType]}
                    </Badge>
                    {summary.created_at && (
                      <span className="text-xs text-muted-foreground">
                        Gerado em {format(parseISO(summary.created_at), "dd/MM/yyyy 'as' HH:mm", { locale: ptBR })}
                      </span>
                    )}
                  </div>
                </div>
              </CardHeader>

              <CardContent className="space-y-6">
                {/* Texto do Resumo */}
                {summary.summary_text && (
                  <div className="p-4 bg-slate-50 rounded-lg border border-slate-100">
                    <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-line">
                      {summary.summary_text}
                    </p>
                  </div>
                )}

                {/* Grid de 6 Blocos - Layout 2x3 */}
                <div className="grid gap-4 md:grid-cols-2">
                  {/* 1. Eventos Importantes */}
                  <div className="p-4 bg-blue-50 rounded-lg border border-blue-100">
                    <h4 className="font-medium text-sm mb-3 flex items-center gap-2 text-blue-800">
                      <CheckCircle2 className="h-4 w-4" />
                      Eventos Importantes
                    </h4>
                    {summary.key_events && (summary.key_events as string[]).length > 0 ? (
                      <ul className="space-y-2">
                        {(summary.key_events as string[]).slice(0, 5).map((event, i) => (
                          <li
                            key={i}
                            className="text-sm text-blue-700 flex items-start gap-2"
                          >
                            <span className="text-blue-400 mt-1 flex-shrink-0">•</span>
                            <span>{event}</span>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-sm text-blue-400 italic">Nenhum evento registrado</p>
                    )}
                  </div>

                  {/* 2. Preocupacoes Identificadas */}
                  <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                    <h4 className="font-medium text-sm mb-3 flex items-center gap-2 text-amber-800">
                      <AlertTriangle className="h-4 w-4" />
                      Preocupacoes Identificadas
                    </h4>
                    {summary.concerns_identified && (summary.concerns_identified as string[]).length > 0 ? (
                      <ul className="space-y-2">
                        {(summary.concerns_identified as string[]).slice(0, 4).map((concern, i) => (
                          <li
                            key={i}
                            className="text-sm text-amber-700 flex items-start gap-2"
                          >
                            <span className="text-amber-400 mt-1 flex-shrink-0">•</span>
                            <span>{concern}</span>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-sm text-amber-400 italic">Nenhuma preocupacao identificada</p>
                    )}
                  </div>

                  {/* 3. Analise Geral (Indicadores de Bem-Estar com scores) */}
                  <div className="p-4 bg-gradient-to-br from-rose-50 to-pink-50 rounded-lg border border-rose-100">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="font-medium text-sm flex items-center gap-2 text-rose-800">
                        <Heart className="h-4 w-4" />
                        Analise Geral
                      </h4>
                      {moodAnalysis?.trendDirection && (
                        <div className="flex items-center gap-1 text-xs text-rose-600">
                          {getTrendIcon(moodAnalysis.trendDirection)}
                          <span>{translateValue(moodAnalysis.trendDirection)}</span>
                        </div>
                      )}
                    </div>
                    {hasScores ? (
                      <div className="space-y-3">
                        <ScoreBar
                          score={moodAnalysis?.averageMood}
                          label="Humor"
                          icon={Heart}
                          color="text-rose-500"
                        />
                        <ScoreBar
                          score={moodAnalysis?.averageSleep}
                          label="Sono"
                          icon={Moon}
                          color="text-indigo-500"
                        />
                        <ScoreBar
                          score={moodAnalysis?.averageAppetite}
                          label="Alimentacao"
                          icon={Utensils}
                          color="text-orange-500"
                        />
                        <ScoreBar
                          score={moodAnalysis?.averageSocial}
                          label="Interacao Social"
                          icon={Users}
                          color="text-blue-500"
                        />
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {moodAnalysis?.overall && (
                          <p className="text-sm text-rose-700">
                            <strong>Estado:</strong> {translateValue(moodAnalysis.overall)}
                          </p>
                        )}
                        {moodAnalysis?.description && (
                          <p className="text-sm text-rose-600">{moodAnalysis.description}</p>
                        )}
                        {!moodAnalysis?.overall && !moodAnalysis?.description && (
                          <p className="text-sm text-rose-400 italic">Sem dados de analise</p>
                        )}
                      </div>
                    )}
                  </div>

                  {/* 4. Padroes de Sono */}
                  <div className="p-4 bg-gradient-to-br from-teal-50 to-cyan-50 rounded-lg border border-teal-100">
                    <h4 className="font-medium text-sm mb-3 flex items-center gap-2 text-teal-800">
                      <Moon className="h-4 w-4" />
                      Padroes de Sono
                    </h4>
                    {sleepPatterns && (sleepPatterns.quality || sleepPatterns.duration || sleepPatterns.issues) ? (
                      <div className="space-y-3">
                        <div className="flex flex-wrap gap-2">
                          {sleepPatterns.quality && (
                            <Badge className="bg-teal-100 text-teal-700 border-teal-200">
                              Qualidade: {translateValue(sleepPatterns.quality)}
                            </Badge>
                          )}
                          {sleepPatterns.duration && (
                            <Badge className="bg-teal-100 text-teal-700 border-teal-200">
                              Duracao: {translateValue(sleepPatterns.duration)}
                            </Badge>
                          )}
                          {(sleepPatterns.averageHours || sleepPatterns.average_hours) && (
                            <Badge className="bg-teal-100 text-teal-700 border-teal-200">
                              Media: {sleepPatterns.averageHours || sleepPatterns.average_hours}h
                            </Badge>
                          )}
                        </div>
                        {sleepPatterns.issues && sleepPatterns.issues.length > 0 && (
                          <div>
                            <p className="text-xs font-medium text-teal-700 mb-1">Observacoes:</p>
                            <p className="text-sm text-teal-600">{sleepPatterns.issues.join(', ')}</p>
                          </div>
                        )}
                        {sleepPatterns.notes && (
                          <p className="text-sm text-teal-600 italic">{sleepPatterns.notes}</p>
                        )}
                      </div>
                    ) : (
                      <p className="text-sm text-teal-400 italic">Sem dados de sono registrados</p>
                    )}
                  </div>

                  {/* 5. Niveis de Atividade */}
                  <div className="p-4 bg-gradient-to-br from-orange-50 to-amber-50 rounded-lg border border-orange-100">
                    <h4 className="font-medium text-sm mb-3 flex items-center gap-2 text-orange-800">
                      <Activity className="h-4 w-4" />
                      Niveis de Atividade
                    </h4>
                    {activityLevels && (activityLevels.physical || activityLevels.social || activityLevels.cognitive) ? (
                      <div className="space-y-2">
                        <ActivityLevel
                          level={activityLevels.physical}
                          label="Atividade Fisica"
                          icon={Dumbbell}
                        />
                        <ActivityLevel
                          level={activityLevels.social}
                          label="Engajamento Social"
                          icon={MessageCircle}
                        />
                        <ActivityLevel
                          level={activityLevels.cognitive}
                          label="Desempenho Cognitivo"
                          icon={Brain}
                        />
                        {activityLevels.notes && (
                          <p className="text-xs text-orange-600 mt-2 italic">{activityLevels.notes}</p>
                        )}
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {activityLevels?.overall && (
                          <p className="text-sm text-orange-700">
                            <strong>Geral:</strong> {translateValue(activityLevels.overall)}
                          </p>
                        )}
                        {activityLevels?.highlights && activityLevels.highlights.length > 0 && (
                          <ul className="space-y-1">
                            {activityLevels.highlights.map((h, i) => (
                              <li key={i} className="text-sm text-orange-600 flex items-start gap-2">
                                <span className="text-orange-400 mt-1">•</span>
                                <span>{h}</span>
                              </li>
                            ))}
                          </ul>
                        )}
                        {!activityLevels?.overall && (!activityLevels?.highlights || activityLevels.highlights.length === 0) && (
                          <p className="text-sm text-orange-400 italic">Sem dados de atividade</p>
                        )}
                      </div>
                    )}
                  </div>

                  {/* 6. Aderencia Medicamentosa */}
                  <div className="p-4 bg-gradient-to-br from-violet-50 to-purple-50 rounded-lg border border-violet-100">
                    <h4 className="font-medium text-sm mb-3 flex items-center gap-2 text-violet-800">
                      <Pill className="h-4 w-4" />
                      Aderencia Medicamentosa
                    </h4>
                    {medicationAdherence && (medicationAdherence.adherenceLevel || medicationAdherence.mentioned || medicationAdherence.status) ? (
                      <div className="space-y-3">
                        <div className="flex flex-wrap gap-2">
                          {medicationAdherence.adherenceLevel && (
                            <Badge
                              className={`${
                                medicationAdherence.adherenceLevel === 'good'
                                  ? 'bg-green-100 text-green-700 border-green-200'
                                  : medicationAdherence.adherenceLevel === 'moderate'
                                    ? 'bg-yellow-100 text-yellow-700 border-yellow-200'
                                    : medicationAdherence.adherenceLevel === 'poor'
                                      ? 'bg-red-100 text-red-700 border-red-200'
                                      : 'bg-violet-100 text-violet-700 border-violet-200'
                              }`}
                            >
                              Aderencia: {translateValue(medicationAdherence.adherenceLevel)}
                            </Badge>
                          )}
                          {medicationAdherence.status && !medicationAdherence.adherenceLevel && (
                            <Badge className="bg-violet-100 text-violet-700 border-violet-200">
                              {translateValue(medicationAdherence.status)}
                            </Badge>
                          )}
                          {medicationAdherence.percentage !== undefined && (
                            <Badge className="bg-violet-100 text-violet-700 border-violet-200">
                              {medicationAdherence.percentage}%
                            </Badge>
                          )}
                        </div>
                        {medicationAdherence.mentioned && Array.isArray(medicationAdherence.mentioned) && medicationAdherence.mentioned.length > 0 && (
                          <div>
                            <p className="text-xs font-medium text-violet-700 mb-1">Medicamentos mencionados:</p>
                            <p className="text-sm text-violet-600">{medicationAdherence.mentioned.join(', ')}</p>
                          </div>
                        )}
                        {medicationAdherence.concerns && medicationAdherence.concerns.length > 0 && (
                          <div className="p-2 bg-amber-50 rounded border border-amber-200">
                            <p className="text-xs font-medium text-amber-700 mb-1">Preocupacoes:</p>
                            <p className="text-sm text-amber-600">{medicationAdherence.concerns.join(', ')}</p>
                          </div>
                        )}
                        {medicationAdherence.notes && (
                          <p className="text-sm text-violet-600 italic">{medicationAdherence.notes}</p>
                        )}
                      </div>
                    ) : (
                      <p className="text-sm text-violet-400 italic">Sem dados de medicacao</p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          )
        })
      )}
    </div>
  )
}

function SummariesSkeleton() {
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-10 w-40" />
      </div>
      {[1, 2].map((i) => (
        <Card key={i}>
          <CardHeader>
            <Skeleton className="h-6 w-64" />
          </CardHeader>
          <CardContent className="space-y-4">
            <Skeleton className="h-20 w-full" />
            <div className="grid gap-4 md:grid-cols-2">
              <Skeleton className="h-48" />
              <Skeleton className="h-48" />
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              <Skeleton className="h-32" />
              <Skeleton className="h-32" />
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  )
}
